/* eslint-disable @typescript-eslint/no-shadow */
import { useInfiniteQuery } from '@tanstack/react-query';
import config from 'config';

import makeSiteGraphqlRequest from '@/utils/graphql-request';

interface Base {
  id: string;
}

export interface Pagination<T> {
  total: number;
  page: number;
  docs: T[];
}

interface Options<T> {
  limit?: number;
  page?: number;
  formatFn: (data: any) => Pagination<T>;
  enabled?: boolean;
  queryKey?: string;
}

export async function makeQuery<T>(
  query: string,
  variables: any,
  page: number,
  limit: number,
  formatFn?: (data: any) => Pagination<T>
) {
  try {
    return await makeSiteGraphqlRequest(
      config,
      query,
      { ...variables, page, limit },
      formatFn
    );
  } catch (e) {
    console.error(e);

    const error = e as any;
    // handle graphql errors
    if (error.response?.errors) {
      throw new Error(error.response.errors[0].message);
    } else {
      throw e;
    }
  }
}

export const getNextPageParam = (
  lastPage: { total: number; page: number },
  allPages: string | any[],
  limit: number
) => {
  if (!lastPage) return null;

  const total = lastPage?.total || 0;
  const totalFetched = allPages.length * limit;
  if (totalFetched < total) {
    // eslint-disable-next-line no-unsafe-optional-chaining
    return lastPage?.page + 1;
  }

  return null;
};

export default function usePagination<T extends Base>(
  query: string,
  variables: any,
  options: Options<T>
) {
  const limit = options.limit || 12;
  const enabled = options.hasOwnProperty('enabled') ? options.enabled : true;

  async function fetchData({ pageParam = 1 }) {
    return makeQuery(query, variables, pageParam, limit, options.formatFn);
  }

  const {
    data,
    isLoading,
    isFetchingNextPage,
    error,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetchedAfterMount,
  } = useInfiniteQuery(
    [options?.queryKey || 'pagination', query, variables],
    fetchData,
    {
      enabled,
      getNextPageParam: (lastPage: any, allPages: any) =>
        getNextPageParam(lastPage, allPages, limit),
    }
  );

  const concatData = data?.pages?.reduce(
    (acc: { docs: any; total: number }, page: { docs: any; total: number }) => {
      if (!page) return acc;

      const data = [...acc.docs, ...page.docs];
      return {
        ...page,
        docs: data,
      };
    },
    { docs: [], total: 0 }
  ) as any;

  return {
    data: concatData as Pagination<T>,
    loading: isLoading,
    isFetchingNextPage,
    error: error as Error,
    hasMore: hasNextPage,
    fetchData: refetch,
    fetchMore: fetchNextPage,
    isFetchedAfterMount,
  };
}
